import type * as D3 from 'd3'

import {
    clamp_deviation,
    format_deviation,
    get_deviation_config,
    get_deviation_rating,
    is_deviation_clipped,
} from './deviation'

/**
 * Bearbeitungszustand eines Bereichs.
 *
 * Nur 'complete' liefert einen endgültigen Saldo. Bei 'running' und 'pending'
 * ist die effektive Arbeitszeit noch unvollständig, der Balken also eine Vorschau.
 */
export type Deviation_State = 'complete' | 'running' | 'pending'

/**
 * Eine Gruppe der Visualisierung, also ein Bereich oder die Gesamtzeiten.
 */
export interface Deviation_Group {
    label: string

    /**
     * Slug des Bereichs, z. B. 'cutting'. Landet als data-project-status an der
     * Überschrift und setzt darüber die Statusfarbe. Bleibt leer bei Gruppen
     * ohne Bereichsbezug wie den Gesamtzeiten.
     */
    slug?: string
    delta_offer: number
    delta_estimate: number
    tolerance?: number
    divider?: boolean

    /**
     * Absolute Werte in Minuten für die Tooltips.
     */
    offer?: number
    estimate?: number
    net?: number

    /**
     * Ob die jeweilige Prognose gepflegt ist. Fehlt sie, rechnet der Saldo mit 0,
     * was der Tooltip kenntlich macht.
     */
    has_offer?: boolean
    has_estimate?: boolean

    /**
     * Angegebener Grund für die Abweichung.
     */
    reason?: string

    /**
     * Bearbeitungszustand des Bereichs. Standard ist 'complete'.
     *
     * Bei 'running' und 'pending' wird ein negativer Saldo neutral dargestellt:
     * Eingesparte Zeit lässt sich erst beurteilen, wenn alle Zeiten gebucht sind.
     * Ein positiver Saldo bleibt farbig, denn eine überschrittene Prognose bleibt
     * überschritten.
     */
    state?: Deviation_State

    /**
     * Eigene Beschriftung hinter dem Gruppennamen. Überschreibt den aus dem
     * Zustand abgeleiteten Text, ohne die Farbgebung zu beeinflussen.
     */
    state_label?: string

    /**
     * Tonalität des Badges. 'warning' für Mängel, die jemand beheben sollte,
     * sonst neutral. Standard ist 'neutral'.
     */
    state_tone?: 'neutral' | 'warning'

    /**
     * Anzahl noch offener Bereiche, nur bei den Gesamtzeiten. Die Summen sind
     * dann vorläufig, abgeblendet wird die Gruppe aber nicht: Ein durchgehend
     * blasser Chart wirkt wie ein Fehler.
     */
    open_areas?: number

    /**
     * Anzahl der Bereiche ohne gepflegte Prognose, nur bei den Gesamtzeiten.
     */
    missing_offer?: number
    missing_estimate?: number

    /**
     * Ob unter der Gruppe eine Skalenzeile erscheint. Standard ist true. Bei
     * aufeinanderfolgenden Gruppen genügt eine Skala unter der letzten, dann
     * lesen sie sich als eine zusammenhängende Grafik.
     */
    scale?: boolean
}

/**
 * Eine Spur innerhalb einer Gruppe.
 */
interface Deviation_Lane {
    label: string
    minutes: number
    variant: 'offer' | 'estimate'
    forecast?: number
    has_forecast: boolean
    missing: number
}

/**
 * Rendert divergierende Balken für Salden gegenüber Angebot und Schätzung.
 *
 * Gegenstück zur E-Mail-Darstellung in Goehler_Tool\Public\Mail\Deviation_Bars.
 * Schwellenwerte und Bewertung kommen aus deviation.ts und damit aus derselben
 * Quelle wie im Bericht. Farben liegen ausschließlich im SCSS und werden über
 * das Attribut data-status angesprochen.
 *
 * Die Breite wird aus dem Container gemessen und über einen ResizeObserver
 * verfolgt, die Skala bleibt dabei in Minuten fixiert.
 */
export class Deviation_Chart {

    /**
     * Breite der Spur-Beschriftung in Pixel.
     */
    private static readonly LABEL_WIDTH = 100

    /**
     * Breite der Randspalten für die Clipping-Pfeile in Pixel.
     */
    private static readonly GUTTER_WIDTH = 18

    /**
     * Höhe eines Balkens in Pixel.
     */
    private static readonly BAR_HEIGHT = 22

    /**
     * Abstand zwischen den beiden Spuren einer Gruppe in Pixel.
     */
    private static readonly LANE_GAP = 4

    /**
     * Höhe der Überschriftszeile einer Gruppe in Pixel.
     */
    private static readonly GROUP_LABEL_HEIGHT = 40

    /**
     * Höhe der Skalenzeile einer Gruppe in Pixel.
     */
    private static readonly SCALE_HEIGHT = 26

    /**
     * Abstand zwischen zwei Gruppen in Pixel.
     */
    private static readonly GROUP_GAP = 18

    /**
     * Höhe der Farblegende über den Gruppen in Pixel.
     */
    private static readonly LEGEND_HEIGHT = 28

    /**
     * Abstand über der Farblegende in Pixel, entspricht 3em bei 16px Grundschrift.
     */
    private static readonly LEGEND_MARGIN_TOP = 48

    /**
     * Zeilenhöhe der Hinweise unter den Gruppen in Pixel.
     */
    private static readonly NOTE_LINE_HEIGHT = 24

    /**
     * Breite der Mittellinie in Pixel.
     */
    private static readonly CENTER_WIDTH = 2

    /**
     * Breite der Kappe am Ende eines geclippten Balkens in Pixel.
     */
    private static readonly CAP_WIDTH = 6

    /**
     * Mindestbreite eines Balkens mit einem Saldo ungleich null.
     *
     * Ohne sie verschwindet die Farbe bei kleinen Salden und schmalen Containern,
     * womit die Aussage der Grafik verloren ginge.
     */
    private static readonly MIN_BAR_WIDTH = 3

    /**
     * Mindestabstand zweier beschrifteter Ticks in Pixel.
     */
    private static readonly MIN_LABEL_SPACING = 72

    /**
     * Breite eines Farbmusters in der Farblegende in Pixel.
     */
    private static readonly SWATCH_WIDTH = 48

    /**
     * Höhe eines Farbmusters in der Farblegende in Pixel.
     */
    private static readonly SWATCH_HEIGHT = 16

    /**
     * Abstand zwischen den beiden Musterzeilen in Pixel.
     */
    private static readonly SWATCH_GAP = 4

    /**
     * Höhe der Spaltenbeschriftung über den Farbmustern in Pixel.
     */
    private static readonly SWATCH_HEADER_HEIGHT = 20

    /**
     * Abstand über der Farblegende in Pixel.
     */
    private static readonly SWATCH_MARGIN_TOP = 24

    /**
     * Spalten der Farblegende in Reihenfolge der Skala.
     */
    private static readonly SWATCH_COLUMNS: Array<{ status: string, label: string }> = [
        { status: 'under', label: 'Zeit eingespart' },
        { status: 'neutral', label: 'keine Aussage möglich' },
        { status: 'tolerance', label: 'im Toleranzbereich' },
        { status: 'over', label: 'Mehrzeit' },
    ]

    /**
     * Beschriftung der Vorschau-Zustände an der Bereichs-Überschrift.
     */
    private static readonly STATE_LABELS: Record<string, string> = {
        running: 'läuft',
        pending: 'nicht gestartet',
    }

    /**
     * Mindestabstand zweier Hilfslinien in Pixel.
     */
    private static readonly MIN_TICK_SPACING = 14

    /**
     * ID der Maske, die die Schraffur des Angebots erzeugt.
     */
    private static readonly HATCH_ID = 'gt-deviation-hatch'

    private container: HTMLElement
    private svg: D3.Selection<SVGSVGElement, unknown, null, undefined> | null = null
    private observer: ResizeObserver | null = null
    private groups: Deviation_Group[] = []
    private with_legend = true
    private with_offer = true
    private is_ready = false

    /**
     * Ebene der Hintergrund-Tracks, ganz unten.
     */
    private layer_track: D3.Selection<SVGGElement, unknown, null, undefined> | null = null

    /**
     * Ebene der durchgehenden Hilfslinien, über dem Track und unter den Balken.
     */
    private layer_grid: D3.Selection<SVGGElement, unknown, null, undefined> | null = null

    /**
     * Ebene für Balken, Beschriftungen und Mittellinie.
     */
    private layer_main: D3.Selection<SVGGElement, unknown, null, undefined> | null = null

    /**
     * Ebene der unsichtbaren Trefferflächen, ganz oben.
     */
    private layer_hover: D3.Selection<SVGGElement, unknown, null, undefined> | null = null

    /**
     * Tooltip-Element, wird beim ersten Zeigen erzeugt.
     */
    private tooltip: HTMLDivElement | null = null

    /**
     * Handler, der das Tooltip bei einem Tap außerhalb schließt.
     */
    private outside_handler: ( ( event: PointerEvent ) => void ) | null = null

    /**
     * Handler, der das Tooltip beim Scrollen schließt.
     */
    private scroll_handler: ( () => void ) | null = null

    /**
     * Bindet den Chart an einen Container und beobachtet dessen Breite.
     *
     * @param container Element, in das gerendert wird.
     */
    public constructor( container: HTMLElement ) {
        this.container = container

        Deviation_Chart.ensure_hatch()

        this.svg = d3.select( container )
            .append( 'svg' )
            .attr( 'class', 'gt-deviation-chart' )

        this.observer = new ResizeObserver( () => this.render() )
        this.observer.observe( container )

        this.is_ready = true
    }

    /**
     * Übernimmt die Gruppen und rendert neu.
     *
     * @param groups Gruppen in Ausgabe-Reihenfolge.
     *
     * @returns Die Instanz für verkettete Aufrufe.
     */
    public set_groups( groups: Deviation_Group[] ): this {
        this.groups = groups
        this.render()

        return this
    }

    /**
     * Legt fest, ob Farblegende und Hinweise gezeichnet werden.
     *
     * @param with_legend Ob die Erklärungen erscheinen.
     *
     * @returns Die Instanz für verkettete Aufrufe.
     */
    public set_legend( with_legend: boolean ): this {
        this.with_legend = with_legend
        this.render()

        return this
    }

    /**
     * Legt fest, ob die Angebots-Spur gezeichnet wird.
     *
     * @param with_offer Ob das Angebot erscheint.
     *
     * @returns Die Instanz für verkettete Aufrufe.
     */
    public set_offer( with_offer: boolean ): this {
        this.with_offer = with_offer
        this.render()

        return this
    }

    /**
     * Zeichnet die Visualisierung vollständig neu.
     */
    public render(): void {
        if ( ! this.is_ready || ! this.svg ) {
            return
        }

        const width = this.container.clientWidth

        if ( width < 1 || ! this.groups.length ) {
            return
        }

        const track = width - Deviation_Chart.LABEL_WIDTH - 2 * Deviation_Chart.GUTTER_WIDTH

        if ( track < 40 ) {
            return
        }

        const scale = d3.scaleLinear()
            .domain( [ -get_deviation_config().max_minutes, get_deviation_config().max_minutes ] )
            .range( [ 0, track ] )
            .clamp( true )

        const offset = Deviation_Chart.LABEL_WIDTH + Deviation_Chart.GUTTER_WIDTH
        const height = this.get_height()

        this.svg
            .attr( 'width', width )
            .attr( 'height', height )
            .attr( 'viewBox', `0 0 ${ width } ${ height }` )

        // Ein offenes Tooltip zeigt sonst auf einen Balken, den es nicht mehr gibt.
        this.hide_tooltip()

        this.svg.selectAll( '*' ).remove()

        let y = 0

        // Drei Ebenen in Zeichenreihenfolge: Der Track liegt unten, darüber die
        // durchgehenden Hilfslinien, darüber Balken und Beschriftungen. Ein Balken
        // überdeckt die Linien damit, der Track nicht.
        this.layer_track = this.svg.append( 'g' )
            .attr( 'class', 'gt-deviation-chart__layer-track' )

        this.layer_grid = this.svg.append( 'g' )
            .attr( 'class', 'gt-deviation-chart__layer-grid' )

        this.layer_main = this.svg.append( 'g' )
            .attr( 'class', 'gt-deviation-chart__layer-main' )

        this.layer_hover = this.svg.append( 'g' )
            .attr( 'class', 'gt-deviation-chart__layer-hover' )

        const lane_count = this.with_offer ? 2 : 1

        const lanes_height = Deviation_Chart.GROUP_LABEL_HEIGHT +
            lane_count * Deviation_Chart.BAR_HEIGHT +
            ( lane_count - 1 ) * Deviation_Chart.LANE_GAP

        this.get_blocks().forEach( ( block ) => {
            if ( this.with_legend ) {
                y += Deviation_Chart.LEGEND_MARGIN_TOP

                this.render_color_legend( scale, track, y, block )

                y += Deviation_Chart.LEGEND_HEIGHT
            }

            const block_top = y + Deviation_Chart.GROUP_LABEL_HEIGHT
            let lanes_bottom = y

            block.forEach( ( group ) => {
                lanes_bottom = y + lanes_height

                y = this.render_group( group, scale, track, y, width )
            } )

            this.render_grid( scale, offset, block_top, lanes_bottom )
        } )

        if ( this.with_legend ) {
            this.render_notes( width, y )
            this.render_color_swatches( offset, track, y + 2 * Deviation_Chart.NOTE_LINE_HEIGHT )
        }
    }

    /**
     * Löst den ResizeObserver und entfernt das SVG.
     */
    public destroy(): void {
        this.observer?.disconnect()
        this.observer = null

        if ( this.outside_handler ) {
            document.removeEventListener( 'pointerdown', this.outside_handler )
            this.outside_handler = null
        }

        if ( this.scroll_handler ) {
            window.removeEventListener( 'scroll', this.scroll_handler, { capture: true } )
            this.scroll_handler = null
        }

        this.tooltip?.remove()
        this.tooltip = null

        this.svg?.remove()
        this.svg = null

        this.is_ready = false
    }

    /**
     * Legt die Schraffur-Maske einmalig im Dokument an.
     *
     * Die Maske schneidet Streifen aus dem Balken heraus, ohne dessen Farbe zu
     * kennen. Ein gefülltes Pattern wäre dafür ungeeignet, da es seine Farbe
     * selbst mitbringen müsste und damit aus dem SCSS herausfiele.
     */
    private static ensure_hatch(): void {
        if ( document.getElementById( Deviation_Chart.HATCH_ID ) ) {
            return
        }

        const size = 4000

        const svg = d3.select( document.body )
            .append( 'svg' )
            .attr( 'class', 'gt-deviation-chart__defs' )
            .attr( 'width', 0 )
            .attr( 'height', 0 )
            .attr( 'aria-hidden', 'true' )

        const defs = svg.append( 'defs' )

        const pattern = defs.append( 'pattern' )
            .attr( 'id', `${ Deviation_Chart.HATCH_ID }-pattern` )
            .attr( 'width', 6 )
            .attr( 'height', 6 )
            .attr( 'patternUnits', 'userSpaceOnUse' )
            .attr( 'patternTransform', 'rotate(45)' )

        pattern.append( 'rect' )
            .attr( 'width', 6 )
            .attr( 'height', 6 )
            .attr( 'fill', 'black' )

        pattern.append( 'rect' )
            .attr( 'width', 3 )
            .attr( 'height', 6 )
            .attr( 'fill', 'white' )

        defs.append( 'mask' )
            .attr( 'id', Deviation_Chart.HATCH_ID )
            .attr( 'maskUnits', 'userSpaceOnUse' )
            .attr( 'x', 0 )
            .attr( 'y', 0 )
            .attr( 'width', size )
            .attr( 'height', size )
            .append( 'rect' )
            .attr( 'width', size )
            .attr( 'height', size )
            .attr( 'fill', `url(#${ Deviation_Chart.HATCH_ID }-pattern)` )
    }

    /**
     * Rendert die Farblegende aus Mustern unter den Hinweisen.
     *
     * Die Muster nutzen dieselben Klassen und Attribute wie die Balken, damit sie
     * nicht nur ähnlich, sondern identisch aussehen. Eine zweite Farbdefinition
     * würde sonst irgendwann von der ersten abweichen.
     *
     * @param offset Linke Kante des Tracks in Pixel.
     * @param track  Breite des Tracks in Pixel.
     * @param y      Obere Kante der Legende in Pixel.
     */
    private render_color_swatches( offset: number, track: number, y: number ): void {
        const top = y + Deviation_Chart.SWATCH_MARGIN_TOP
        const column_width = track / Deviation_Chart.SWATCH_COLUMNS.length

        const variants: Array<{ label: string, variant: 'offer' | 'estimate' }> = [
            { label: 'Angebot', variant: 'offer' },
            { label: 'Schätzung', variant: 'estimate' },
        ]

        const rows = variants.filter( ( row ) => this.with_offer || row.variant !== 'offer' )

        rows.forEach( ( row, index ) => {
            const row_y = top +
                Deviation_Chart.SWATCH_HEADER_HEIGHT +
                index * ( Deviation_Chart.SWATCH_HEIGHT + Deviation_Chart.SWATCH_GAP )

            this.layer_main!.append( 'text' )
                .attr( 'class', 'gt-deviation-chart__lane-label' )
                .attr( 'x', Deviation_Chart.LABEL_WIDTH - 8 )
                .attr( 'y', row_y + Deviation_Chart.SWATCH_HEIGHT / 2 )
                .attr( 'text-anchor', 'end' )
                .attr( 'dominant-baseline', 'middle' )
                .text( row.label )

            Deviation_Chart.SWATCH_COLUMNS.forEach( ( column, position ) => {
                const center = offset + column_width * ( position + 0.5 )

                const swatch = this.layer_main!.append( 'rect' )
                    .attr( 'class', `gt-deviation-chart__bar gt-deviation-chart__bar--${ row.variant }` )
                    .attr( 'data-status', column.status )
                    .attr( 'x', center - Deviation_Chart.SWATCH_WIDTH / 2 )
                    .attr( 'y', row_y )
                    .attr( 'width', Deviation_Chart.SWATCH_WIDTH )
                    .attr( 'height', Deviation_Chart.SWATCH_HEIGHT )

                if ( row.variant === 'offer' ) {
                    swatch.attr( 'mask', `url(#${ Deviation_Chart.HATCH_ID })` )
                }
            } )
        } )

        Deviation_Chart.SWATCH_COLUMNS.forEach( ( column, position ) => {
            this.layer_main!.append( 'text' )
                .attr( 'class', 'gt-deviation-chart__swatch-label' )
                .attr( 'data-status', column.status )
                .attr( 'x', offset + column_width * ( position + 0.5 ) )
                .attr( 'y', top + Deviation_Chart.SWATCH_HEADER_HEIGHT - 6 )
                .attr( 'text-anchor', 'middle' )
                .text( column.label )
        } )
    }

    /**
     * Teilt die Gruppen in Blöcke auf.
     *
     * Ein Block endet dort, wo eine Skalenzeile steht. Jeder Block bekommt seine
     * eigene Farblegende und durchgehende Hilfslinien.
     *
     * @returns Blöcke in Ausgabe-Reihenfolge.
     */
    private get_blocks(): Deviation_Group[][] {
        const blocks: Deviation_Group[][] = []

        let current: Deviation_Group[] = []

        this.groups.forEach( ( group, index ) => {
            current.push( group )

            if ( group.scale !== false || index === this.groups.length - 1 ) {
                blocks.push( current )

                current = []
            }
        } )

        return blocks
    }

    /**
     * Baut den Inhalt des Tooltips als DOM-Knoten.
     *
     * Bewusst ohne innerHTML: Der angegebene Grund ist eine Nutzereingabe und
     * würde sonst als Markup interpretiert.
     *
     * @param group   Gruppe, zu der die Spur gehört.
     * @param lane    Spur mit Saldo und Variante.
     * @param rating  Bewertung des Saldos.
     * @param clipped Ob der Balken die Skala überschreitet.
     *
     * @returns Fertiges Element für das Tooltip.
     */
    private get_tooltip_content(
        group: Deviation_Group,
        lane: Deviation_Lane,
        rating: string,
        clipped: boolean,
    ): HTMLElement {
        const content = document.createElement( 'div' )

        const title = document.createElement( 'div' )
        title.className = 'chart-tooltip__title'
        title.textContent = `${ group.label } · ${ lane.label }`
        content.appendChild( title )

        const forecast = lane.has_forecast ?
            this.format_duration( lane.forecast ?? 0 ) :
            'nicht gepflegt'

        content.appendChild( this.get_tooltip_row( 'Prognose', forecast ) )
        content.appendChild( this.get_tooltip_row( 'eff. Arbeitszeit', this.format_duration( group.net ?? 0 ) ) )

        const saldo = this.get_tooltip_row( 'Saldo', `${ format_deviation( lane.minutes ) } h` )
        saldo.setAttribute( 'data-status', rating )
        content.appendChild( saldo )

        this.get_tooltip_notes( group, lane, clipped ).forEach( ( text ) => {
            const note = document.createElement( 'div' )
            note.className = 'chart-tooltip__note'
            note.textContent = text
            content.appendChild( note )
        } )

        return content
    }

    /**
     * Baut eine Zeile aus Beschriftung und Wert.
     *
     * @param label Beschriftung.
     * @param value Wert.
     *
     * @returns Zeilen-Element.
     */
    private get_tooltip_row( label: string, value: string ): HTMLElement {
        const row = document.createElement( 'div' )
        row.className = 'chart-tooltip__row'

        const key = document.createElement( 'span' )
        key.textContent = label

        const val = document.createElement( 'span' )
        val.textContent = value

        row.appendChild( key )
        row.appendChild( val )

        return row
    }

    /**
     * Sammelt die Hinweiszeilen, die für eine Spur zutreffen.
     *
     * Die Bewertung wird hier neu ermittelt und nicht von der Darstellung
     * übernommen: Bei laufenden Bereichen ist der Balken immer neutral, der
     * Tooltip soll aber den tatsächlichen Saldo beschreiben.
     *
     * @param group   Gruppe, zu der die Spur gehört.
     * @param lane    Spur mit Saldo und Variante.
     * @param clipped Ob der Balken die Skala überschreitet.
     *
     * @returns Hinweistexte in Ausgabe-Reihenfolge.
     */
    private get_tooltip_notes(
        group: Deviation_Group,
        lane: Deviation_Lane,
        clipped: boolean,
    ): string[] {
        const notes: string[] = []
        const maximum = get_deviation_config().max_minutes
        const rating = get_deviation_rating( lane.minutes, group.tolerance ?? 0 )

        // Bedeutung des Saldos immer zuerst, damit auch ein unauffälliger Balken
        // erklärt wird und nicht nur die Sonderfälle.
        if ( rating === 'over' ) {
            notes.push( 'Mehrzeit gegenüber der Prognose' )
        }

        if ( rating === 'under' ) {
            notes.push( 'Zeit eingespart gegenüber der Prognose' )
        }

        if ( rating === 'tolerance' ) {
            notes.push( `im Toleranzbereich von ±${ this.format_hours( group.tolerance ?? 0 ) } h` )
        }

        if ( rating === 'neutral' ) {
            notes.push( lane.has_forecast ? 'Prognose exakt getroffen' : 'keine Abweichung messbar' )
        }

        if ( clipped ) {
            notes.push( `Balken endet bei ${ this.format_hours( maximum ) } h, der Saldo liegt darüber` )
        }

        if ( ! lane.has_forecast ) {
            notes.push( 'ohne Prognose zählt die gesamte Arbeitszeit als Abweichung' )
        }

        if ( lane.missing > 0 ) {
            const areas = lane.missing === 1 ? '1 Bereich' : `${ lane.missing } Bereiche`

            notes.push( `${ areas } ohne Prognose, die Summe ist entsprechend unvollständig` )
        }

        if ( group.state === 'running' ) {
            notes.push( 'Bereich läuft noch, der Balken bleibt bis zum Abschluss neutral' )
        }

        if ( group.state === 'pending' ) {
            notes.push( 'Bereich noch nicht gestartet, es sind keine Zeiten gebucht' )
        }

        if ( group.open_areas ) {
            const areas = group.open_areas === 1 ? '1 Bereich ist' : `${ group.open_areas } Bereiche sind`

            notes.push( `${ areas } noch nicht abgeschlossen, die Summen sind vorläufig` )
        }

        if ( lane.variant === 'estimate' && group.reason ) {
            notes.push( `Grund: ${ group.reason }` )
        }

        return notes
    }

    /**
     * Liefert das Tooltip-Element und erzeugt es beim ersten Aufruf.
     *
     * @returns Das Tooltip-Element.
     */
    private get_tooltip(): HTMLDivElement {
        if ( ! this.tooltip ) {
            this.tooltip = document.createElement( 'div' )
            this.tooltip.className = 'chart-tooltip chart-tooltip--deviation'
            this.tooltip.style.position = 'fixed'
            this.tooltip.style.pointerEvents = 'none'
            this.tooltip.style.display = 'none'

            document.body.appendChild( this.tooltip )

            // Auf Touch bleibt das Tooltip stehen, bis daneben getippt wird.
            this.outside_handler = ( event: PointerEvent ) => {
                if ( ! this.container.contains( event.target as Node ) ) {
                    this.hide_tooltip()
                }
            }

            document.addEventListener( 'pointerdown', this.outside_handler )

            // Das Tooltip liegt fix im Viewport und würde beim Scrollen stehen
            // bleiben, während der zugehörige Balken wegwandert. Auf Touch bleibt
            // es mangels pointerleave offen, daher hier schließen.
            this.scroll_handler = () => this.hide_tooltip()

            window.addEventListener( 'scroll', this.scroll_handler, { capture: true, passive: true } )
        }

        return this.tooltip
    }

    /**
     * Zeigt das Tooltip an der Zeigerposition.
     *
     * @param content Inhalt des Tooltips.
     * @param event   Auslösendes Zeiger-Ereignis.
     */
    private show_tooltip( content: HTMLElement, event: PointerEvent ): void {
        const tooltip = this.get_tooltip()

        tooltip.replaceChildren( content )
        tooltip.style.display = 'block'

        this.move_tooltip( event )
    }

    /**
     * Verschiebt das Tooltip an die Zeigerposition.
     *
     * @param event Auslösendes Zeiger-Ereignis.
     */
    private move_tooltip( event: PointerEvent ): void {
        if ( ! this.tooltip ) {
            return
        }

        this.tooltip.style.left = `${ event.clientX }px`
        this.tooltip.style.top = `${ event.clientY - 12 }px`
        this.tooltip.style.transform = 'translate(-50%, -100%)'
    }

    /**
     * Blendet das Tooltip aus.
     */
    private hide_tooltip(): void {
        if ( this.tooltip ) {
            this.tooltip.style.display = 'none'
        }
    }

    /**
     * Formatiert Minuten als Dauer mit Einheit, z. B. '1:30 h'.
     *
     * Negative Werte behalten ihr Vorzeichen. Sie sollten in der Praxis nicht
     * vorkommen, entstehen aber durch Korrekturbuchungen und dürfen dann nicht
     * als positiver Wert erscheinen.
     *
     * @param minutes Minuten.
     *
     * @returns Formatierte Dauer.
     */
    private format_duration( minutes: number ): string {
        const sign = minutes < 0 ? '-' : ''

        return `${ sign }${ this.format_hours( minutes ) } h`
    }

    /**
     * Berechnet die Gesamthöhe aller Gruppen in Pixel.
     *
     * @returns Höhe in Pixel.
     */
    private get_height(): number {
        const lanes = this.with_offer ? 2 : 1

        const base = Deviation_Chart.GROUP_LABEL_HEIGHT +
            lanes * Deviation_Chart.BAR_HEIGHT +
            ( lanes - 1 ) * Deviation_Chart.LANE_GAP +
            Deviation_Chart.GROUP_GAP

        const groups = this.groups.reduce(
            ( total, group ) => total + base + ( group.scale === false ? 0 : Deviation_Chart.SCALE_HEIGHT ),
            0,
        )

        const swatches = Deviation_Chart.SWATCH_MARGIN_TOP +
            Deviation_Chart.SWATCH_HEADER_HEIGHT +
            lanes * Deviation_Chart.SWATCH_HEIGHT +
            ( lanes - 1 ) * Deviation_Chart.SWATCH_GAP

        const legend = this.with_legend ?
            this.get_blocks().length * ( Deviation_Chart.LEGEND_MARGIN_TOP + Deviation_Chart.LEGEND_HEIGHT ) +
            2 * Deviation_Chart.NOTE_LINE_HEIGHT +
            swatches :
            0

        return groups + legend
    }

    /**
     * Rendert die Farblegende über den Gruppen.
     *
     * Die Beschriftungen sitzen an den Positionen, die sie erklären: eingesparte
     * Zeit links der Mittellinie, der Toleranzbereich auf ihr, Mehrzeit rechts.
     *
     * @param scale Skala von Minuten auf Pixel.
     * @param track Breite des Tracks in Pixel.
     * @param top   Obere Kante der Legende in Pixel.
     * @param block Gruppen des Blocks, für den die Legende gilt.
     */
    private render_color_legend(
        scale: D3.ScaleLinear<number, number>,
        track: number,
        top: number,
        block: Deviation_Group[],
    ): void {
        const offset = Deviation_Chart.LABEL_WIDTH + Deviation_Chart.GUTTER_WIDTH
        const y = top + Deviation_Chart.LEGEND_HEIGHT - 8

        // Mittig über der jeweiligen Hälfte, also auf halbem Weg zwischen
        // Skalenrand und Mittellinie.
        const entries: Array<{ label: string, status: string, x: number }> = [
            { label: 'Zeit eingespart', status: 'under', x: offset + track / 4 },
            { label: 'Mehrzeit', status: 'over', x: offset + track * 3 / 4 },
        ]

        // Der Toleranzbereich erscheint nur, wo er auch gilt.
        if ( block.some( ( group ) => ( group.tolerance ?? 0 ) > 0 ) ) {
            entries.push( { label: 'im Toleranzbereich', status: 'tolerance', x: offset + scale( 0 ) } )
        }

        const legend = this.layer_main!.append( 'g' )
            .attr( 'class', 'gt-deviation-chart__legend' )

        entries.forEach( ( entry ) => {
            legend.append( 'text' )
                .attr( 'class', 'gt-deviation-chart__legend-label' )
                .attr( 'data-status', entry.status )
                .attr( 'x', entry.x )
                .attr( 'y', y )
                .attr( 'text-anchor', 'middle' )
                .text( entry.label )
        } )
    }

    /**
     * Rendert die erklärenden Hinweise unter den Gruppen.
     *
     * @param width Gesamtbreite in Pixel.
     * @param y     Obere Kante der Hinweise in Pixel.
     */
    private render_notes( width: number, y: number ): void {
        const maximum = get_deviation_config().max_minutes

        const lines = [
            'Saldo = effektive Arbeitszeit - Prognose',
            `\u25C0 \u25B6 Abweichung über ${ this.format_hours( maximum ) } h`,
        ]

        lines.forEach( ( line, index ) => {
            this.layer_main!.append( 'text' )
                .attr( 'class', 'gt-deviation-chart__note' )
                .attr( 'x', width / 2 )
                .attr( 'y', y + ( index + 1 ) * Deviation_Chart.NOTE_LINE_HEIGHT - 6 )
                .attr( 'text-anchor', 'middle' )
                .text( line )
        } )
    }

    /**
     * Rendert eine Gruppe und gibt die y-Position der nächsten zurück.
     *
     * @param group Gruppe mit Beschriftung und Salden.
     * @param scale Skala von Minuten auf Pixel.
     * @param track Breite des Tracks in Pixel.
     * @param y     Obere Kante der Gruppe in Pixel.
     * @param width Gesamtbreite in Pixel.
     *
     * @returns Obere Kante der nächsten Gruppe.
     */
    private render_group(
        group: Deviation_Group,
        scale: D3.ScaleLinear<number, number>,
        track: number,
        y: number,
        width: number,
    ): number {
        const offset = Deviation_Chart.LABEL_WIDTH + Deviation_Chart.GUTTER_WIDTH
        const center = offset + scale( 0 )
        const tolerance = group.tolerance ?? 0

        const root = this.layer_main!.append( 'g' )
            .attr( 'class', 'gt-deviation-chart__group' )
            .attr( 'data-state', group.state ?? 'complete' )

        // Grundlinie der Überschriftszeile. Der Rest der Zeilenhöhe bleibt als
        // Abstand zu den Tracks darunter.
        const baseline = y + Deviation_Chart.GROUP_LABEL_HEIGHT - 16

        root.append( 'text' )
            .attr( 'class', 'gt-deviation-chart__group-label' )
            .attr( 'data-project-status', group.slug ?? null )
            .attr( 'x', 0 )
            .attr( 'y', baseline )
            .text( group.label )

        const state_label = group.state_label ?? Deviation_Chart.STATE_LABELS[ group.state ?? 'complete' ]

        if ( state_label ) {
            this.render_state_badge( root, state_label, group.state_tone ?? 'neutral', offset, track, y )
        }

        // Angedeutete Mittellinie über der ersten Spur.
        root.append( 'rect' )
            .attr( 'class', 'gt-deviation-chart__center' )
            .attr( 'x', center )
            .attr( 'y', y + Deviation_Chart.GROUP_LABEL_HEIGHT - 6 )
            .attr( 'width', Deviation_Chart.CENTER_WIDTH )
            .attr( 'height', 6 )

        const all_lanes: Deviation_Lane[] = [
            {
                label: 'Angebot',
                minutes: group.delta_offer,
                variant: 'offer',
                forecast: group.offer,
                has_forecast: group.has_offer !== false,
                missing: group.missing_offer ?? 0,
            },
            {
                label: 'Schätzung',
                minutes: group.delta_estimate,
                variant: 'estimate',
                forecast: group.estimate,
                has_forecast: group.has_estimate !== false,
                missing: group.missing_estimate ?? 0,
            },
        ]

        const lanes = all_lanes.filter( ( lane ) => this.with_offer || lane.variant !== 'offer' )

        let lane_y = y + Deviation_Chart.GROUP_LABEL_HEIGHT

        lanes.forEach( ( lane, index ) => {
            this.render_lane( root, group, lane, scale, track, offset, lane_y, tolerance )

            lane_y += Deviation_Chart.BAR_HEIGHT

            if ( index < lanes.length - 1 ) {
                root.append( 'rect' )
                    .attr( 'class', 'gt-deviation-chart__center' )
                    .attr( 'x', center )
                    .attr( 'y', lane_y )
                    .attr( 'width', Deviation_Chart.CENTER_WIDTH )
                    .attr( 'height', Deviation_Chart.LANE_GAP )

                lane_y += Deviation_Chart.LANE_GAP
            }
        } )

        if ( group.scale !== false ) {
            this.render_scale( root, scale, offset, lane_y )

            lane_y += Deviation_Chart.SCALE_HEIGHT
        }

        if ( group.divider ) {
            root.append( 'line' )
                .attr( 'class', 'gt-deviation-chart__divider' )
                .attr( 'x1', 0 )
                .attr( 'x2', width )
                .attr( 'y1', lane_y + Deviation_Chart.GROUP_GAP / 2 )
                .attr( 'y2', lane_y + Deviation_Chart.GROUP_GAP / 2 )
        }

        return lane_y + Deviation_Chart.GROUP_GAP
    }

    /**
     * Rendert den Zustands-Badge rechtsbündig über dem Track.
     *
     * Umgesetzt als foreignObject mit einem HTML-Element darin. SVG kennt weder
     * Innenabstand noch Eckenradius, ein Badge aus Rechteck und Text müsste die
     * Textbreite messen und wäre je nach Schrift ungleichmäßig. Als HTML
     * übernimmt das Layout schlicht das CSS.
     *
     * @param root   Gruppe, in die gezeichnet wird.
     * @param text   Beschriftung des Badges.
     * @param tone   Tonalität, steuert die Farbgebung über data-tone.
     * @param offset Linke Kante des Tracks in Pixel.
     * @param track  Breite des Tracks in Pixel.
     * @param y      Obere Kante der Überschriftszeile in Pixel.
     */
    private render_state_badge(
        root: D3.Selection<SVGGElement, unknown, null, undefined>,
        text: string,
        tone: string,
        offset: number,
        track: number,
        y: number,
    ): void {
        root.append( 'foreignObject' )
            .attr( 'x', offset )
            .attr( 'y', y )
            .attr( 'width', track )
            .attr( 'height', Deviation_Chart.GROUP_LABEL_HEIGHT - 8 )
            .append( 'xhtml:div' )
            .attr( 'class', 'gt-deviation-chart__group-state' )
            .append( 'xhtml:span' )
            .attr( 'data-tone', tone )
            .text( text )
    }

    /**
     * Rendert eine Spur samt Track, Ticks, Balken und Clipping-Pfeil.
     *
     * @param root      Gruppe, in die gezeichnet wird.
     * @param group     Gruppe, zu der die Spur gehört.
     * @param lane      Spur mit Saldo und Variante.
     * @param scale     Skala von Minuten auf Pixel.
     * @param track     Breite des Tracks in Pixel.
     * @param offset    Linke Kante des Tracks in Pixel.
     * @param y         Obere Kante der Spur in Pixel.
     * @param tolerance Toleranz in Minuten.
     */
    private render_lane(
        root: D3.Selection<SVGGElement, unknown, null, undefined>,
        group: Deviation_Group,
        lane: Deviation_Lane,
        scale: D3.ScaleLinear<number, number>,
        track: number,
        offset: number,
        y: number,
        tolerance: number,
    ): void {
        const rating = this.get_display_rating( group, lane.minutes, tolerance )
        const clipped = is_deviation_clipped( lane.minutes )
        const center = offset + scale( 0 )

        root.append( 'text' )
            .attr( 'class', 'gt-deviation-chart__lane-label' )
            .attr( 'x', Deviation_Chart.LABEL_WIDTH - 8 )
            .attr( 'y', y + Deviation_Chart.BAR_HEIGHT / 2 )
            .attr( 'text-anchor', 'end' )
            .attr( 'dominant-baseline', 'middle' )
            .text( lane.label )

        this.layer_track!.append( 'rect' )
            .attr( 'class', 'gt-deviation-chart__track' )
            .attr( 'x', offset )
            .attr( 'y', y )
            .attr( 'width', track )
            .attr( 'height', Deviation_Chart.BAR_HEIGHT )

        const clamped = clamp_deviation( lane.minutes )
        const edge = offset + scale( clamped )

        let bar_x = Math.min( center, edge )
        let bar_width = Math.abs( edge - center )

        if ( lane.minutes !== 0 && bar_width < Deviation_Chart.MIN_BAR_WIDTH ) {
            bar_width = Deviation_Chart.MIN_BAR_WIDTH
            bar_x = lane.minutes > 0 ? center : center - bar_width
        }

        if ( lane.minutes === 0 ) {
            bar_x = center
            bar_width = Deviation_Chart.CENTER_WIDTH
        }

        const bar = root.append( 'rect' )
            .attr( 'class', `gt-deviation-chart__bar gt-deviation-chart__bar--${ lane.variant }` )
            .attr( 'data-status', rating )
            .attr( 'x', bar_x )
            .attr( 'y', y )
            .attr( 'width', bar_width )
            .attr( 'height', Deviation_Chart.BAR_HEIGHT )

        // Schraffur des Angebots als Maske, nicht über CSS: Ein Verweis auf eine
        // SVG-Maske per mask-image wird nicht von allen Browsern unterstützt,
        // das Präsentationsattribut dagegen schon.
        if ( lane.variant === 'offer' ) {
            bar.attr( 'mask', `url(#${ Deviation_Chart.HATCH_ID })` )
        }

        if ( clipped ) {
            const cap_x = lane.minutes > 0 ?
                bar_x + bar_width - Deviation_Chart.CAP_WIDTH :
                bar_x

            root.append( 'rect' )
                .attr( 'class', `gt-deviation-chart__cap gt-deviation-chart__cap--${ lane.variant }` )
                .attr( 'data-status', rating )
                .attr( 'x', cap_x )
                .attr( 'y', y )
                .attr( 'width', Deviation_Chart.CAP_WIDTH )
                .attr( 'height', Deviation_Chart.BAR_HEIGHT )

            this.render_arrow( root, rating, lane.minutes > 0, offset, track, y )
        }

        // Mittellinie zuletzt, damit sie über dem Balken liegt.
        root.append( 'rect' )
            .attr( 'class', 'gt-deviation-chart__center' )
            .attr( 'x', center )
            .attr( 'y', y )
            .attr( 'width', Deviation_Chart.CENTER_WIDTH )
            .attr( 'height', Deviation_Chart.BAR_HEIGHT )

        this.render_hover_target( group, lane, rating, clipped, offset, track, y )
    }

    /**
     * Legt eine unsichtbare Trefferfläche über die volle Spurbreite.
     *
     * Nötig, weil ein Balken nur wenige Pixel breit sein kann und als Ziel für
     * Maus oder Finger nicht taugt.
     *
     * @param group   Gruppe, zu der die Spur gehört.
     * @param lane    Spur mit Saldo und Variante.
     * @param rating  Bewertung des Saldos.
     * @param clipped Ob der Balken die Skala überschreitet.
     * @param offset  Linke Kante des Tracks in Pixel.
     * @param track   Breite des Tracks in Pixel.
     * @param y       Obere Kante der Spur in Pixel.
     */
    private render_hover_target(
        group: Deviation_Group,
        lane: Deviation_Lane,
        rating: string,
        clipped: boolean,
        offset: number,
        track: number,
        y: number,
    ): void {
        this.layer_hover!.append( 'rect' )
            .attr( 'class', 'gt-deviation-chart__hover' )
            .attr( 'x', offset )
            .attr( 'y', y - Deviation_Chart.LANE_GAP )
            .attr( 'width', track )
            .attr( 'height', Deviation_Chart.BAR_HEIGHT + 2 * Deviation_Chart.LANE_GAP )
            .on( 'pointerenter', ( event: PointerEvent ) => {
                this.show_tooltip( this.get_tooltip_content( group, lane, rating, clipped ), event )
            } )
            .on( 'pointermove', ( event: PointerEvent ) => {
                if ( event.pointerType === 'mouse' ) {
                    this.move_tooltip( event )
                }
            } )
            .on( 'pointerleave', ( event: PointerEvent ) => {
                if ( event.pointerType === 'mouse' ) {
                    this.hide_tooltip()
                }
            } )
    }

    /**
     * Ermittelt die Bewertung für die Darstellung.
     *
     * Solange ein Bereich nicht abgeschlossen ist, wird der Balken neutral
     * gezeichnet. Der Saldo beruht dort auf unvollständigen Zeiten und taugt
     * noch nicht für eine Bewertung. Die tatsächlichen Zahlen bleiben im Tooltip
     * sichtbar.
     *
     * @param group     Gruppe, zu der die Spur gehört.
     * @param minutes   Saldo in Minuten.
     * @param tolerance Toleranz in Minuten.
     *
     * @returns Bewertung für die Darstellung.
     */
    private get_display_rating( group: Deviation_Group, minutes: number, tolerance: number ): string {
        if ( ( group.state ?? 'complete' ) !== 'complete' ) {
            return 'neutral'
        }

        return get_deviation_rating( minutes, tolerance )
    }

    /**
     * Rendert die durchgehenden Linien eines Blocks aus mehreren Gruppen.
     *
     * Die Linien laufen über alle Spuren des Blocks hinweg, damit sich die
     * Gruppen als eine zusammenhängende Grafik lesen. Sie kreuzen dabei die
     * Überschriftszeilen, was unkritisch ist: Deren Text liegt links des Tracks.
     *
     * @param scale  Skala von Minuten auf Pixel.
     * @param offset Linke Kante des Tracks in Pixel.
     * @param top    Obere Kante des Blocks in Pixel.
     * @param bottom Untere Kante des Blocks in Pixel.
     */
    private render_grid(
        scale: D3.ScaleLinear<number, number>,
        offset: number,
        top: number,
        bottom: number,
    ): void {
        const config = get_deviation_config()
        const step = this.get_step( scale, config.tick_minutes, Deviation_Chart.MIN_TICK_SPACING )

        for ( let minutes = -config.max_minutes + step; minutes < config.max_minutes; minutes += step ) {
            if ( minutes === 0 ) {
                continue
            }

            this.layer_grid!.append( 'line' )
                .attr( 'class', 'gt-deviation-chart__grid-line' )
                .attr( 'x1', offset + scale( minutes ) )
                .attr( 'x2', offset + scale( minutes ) )
                .attr( 'y1', top )
                .attr( 'y2', bottom )
        }

        this.layer_grid!.append( 'rect' )
            .attr( 'class', 'gt-deviation-chart__center' )
            .attr( 'x', offset + scale( 0 ) )
            .attr( 'y', top )
            .attr( 'width', Deviation_Chart.CENTER_WIDTH )
            .attr( 'height', bottom - top )
    }

    /**
     * Rendert die Skalenzeile einer Gruppe.
     *
     * @param root   Gruppe, in die gezeichnet wird.
     * @param scale  Skala von Minuten auf Pixel.
     * @param offset Linke Kante des Tracks in Pixel.
     * @param y      Obere Kante der Skalenzeile in Pixel.
     */
    private render_scale(
        root: D3.Selection<SVGGElement, unknown, null, undefined>,
        scale: D3.ScaleLinear<number, number>,
        offset: number,
        y: number,
    ): void {
        const config = get_deviation_config()
        const step = this.get_step( scale, config.tick_minutes, Deviation_Chart.MIN_LABEL_SPACING )

        for ( let minutes = -config.max_minutes; minutes <= config.max_minutes; minutes += step ) {
            const anchor = -config.max_minutes === minutes ?
                'start' :
                ( config.max_minutes === minutes ? 'end' : 'middle' )

            root.append( 'text' )
                .attr( 'class', 'gt-deviation-chart__scale-label' )
                .attr( 'x', offset + scale( minutes ) )
                .attr( 'y', y + 17 )
                .attr( 'text-anchor', anchor )
                .text( this.format_scale_label( minutes ) )
        }
    }

    /**
     * Rendert den Pfeil für einen geclippten Balken.
     *
     * @param root   Gruppe, in die gezeichnet wird.
     * @param rating Bewertung des Saldos.
     * @param right  Ob der Pfeil nach rechts zeigt.
     * @param offset Linke Kante des Tracks in Pixel.
     * @param track  Breite des Tracks in Pixel.
     * @param y      Obere Kante der Spur in Pixel.
     */
    private render_arrow(
        root: D3.Selection<SVGGElement, unknown, null, undefined>,
        rating: string,
        right: boolean,
        offset: number,
        track: number,
        y: number,
    ): void {
        const middle = y + Deviation_Chart.BAR_HEIGHT / 2
        const size = 6

        const tip = right ?
            offset + track + Deviation_Chart.GUTTER_WIDTH - 4 :
            offset - Deviation_Chart.GUTTER_WIDTH + 4

        const base = right ? tip - size : tip + size

        root.append( 'path' )
            .attr( 'class', 'gt-deviation-chart__arrow' )
            .attr( 'data-status', rating )
            .attr( 'd', `M ${ tip } ${ middle } L ${ base } ${ middle - size } L ${ base } ${ middle + size } Z` )
    }

    /**
     * Ermittelt den Abstand der Linien so, dass ein Mindestabstand eingehalten wird.
     *
     * Der Wert ist stets ein Vielfaches von tick_minutes, damit Hilfslinien und
     * Beschriftungen auf denselben Positionen sitzen.
     *
     * @param scale   Skala von Minuten auf Pixel.
     * @param base    Kleinster Abstand in Minuten.
     * @param minimum Mindestabstand in Pixel.
     *
     * @returns Abstand in Minuten.
     */
    private get_step( scale: D3.ScaleLinear<number, number>, base: number, minimum: number ): number {
        let step = Math.max( 1, base )

        while ( scale( step ) - scale( 0 ) < minimum ) {
            step += base
        }

        return step
    }

    /**
     * Formatiert Minuten als Stundenangabe ohne Vorzeichen, z. B. '6:00'.
     *
     * @param minutes Minuten.
     *
     * @returns Formatierte Stundenangabe.
     */
    private format_hours( minutes: number ): string {
        const absolute = Math.abs( minutes )

        return `${ Math.floor( absolute / 60 ) }:${ String( absolute % 60 ).padStart( 2, '0' ) }`
    }

    /**
     * Formatiert eine Skalen-Beschriftung mit Vorzeichen.
     *
     * @param minutes Position auf der Skala in Minuten.
     *
     * @returns Beschriftung.
     */
    private format_scale_label( minutes: number ): string {
        if ( minutes === 0 ) {
            return '0'
        }

        const absolute = Math.abs( minutes )
        const hours = Math.floor( absolute / 60 )
        const rest = absolute % 60

        return `${ minutes > 0 ? '+' : '-' }${ hours }:${ String( rest ).padStart( 2, '0' ) }`
    }
}
